import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import validateEmail from "@insite/client-framework/Common/Utilities/validateEmail";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import { addAccount } from "@insite/client-framework/Services/AccountService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import validatePassword from "@insite/client-framework/Store/CommonHandlers/ValidatePassword";
import validateReCaptcha from "@insite/client-framework/Store/Components/ReCaptcha/Handlers/ValidateReCaptcha";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCreateAccountReturnUrl } from "@insite/client-framework/Store/Pages/CreateAccount/CreateAccountSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ReCaptcha from "@insite/content-library/Components/ReCaptcha";
import Button from "@insite/mobius/Button";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem from "@insite/mobius/GridItem";
import Icon from "@insite/mobius/Icon";
import Link from "@insite/mobius/Link";
import TextField from "@insite/mobius/TextField";
import Typography from "@insite/mobius/Typography/Typography";
import React, { ReactNode, RefObject, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { connect, ResolveThunks } from "react-redux";

const mapDispatchToProps = {
    addAccount,
    validatePassword,
    validateReCaptcha: makeHandlerChainAwaitable<{ location: string; recaptchaRef: RefObject<ReCAPTCHA> }, boolean>(
        validateReCaptcha,
    ),
};

const mapStateToProps = (state: ApplicationState) => ({
    accountSettings: getSettingsCollection(state).accountSettings,
    returnUrl: getCreateAccountReturnUrl(state),
});

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface CreateAccountStyles {}

export const CreateAccountStyles: CreateAccountStyles = {};

const styles = CreateAccountStyles;
const phoneRegexp = new RegExp("^[0-9b]+$/");
const StyledForm = getStyledWrapper("form");

export const GlobalCreateAccount: React.FC<Props> = props => {
    const firstNameRequiredFieldMessage = siteMessage("CreateNewAccountInfo_FirstName_Required");
    const lastNameRequiredFieldMessage = siteMessage("CreateNewAccountInfo_LastName_Required");
    const companyNameRequiredFieldMessage = siteMessage("CreateNewAccountInfo_CompanyName_Required");
    const emailRequiredFieldMessage = siteMessage("CreateNewAccountInfo_EmailAddress_Required");
    const emailFieldMessage = siteMessage("CreateNewAccountInfo_EmailAddress_ValidEmail");
    const phoneFieldMessage = siteMessage("CreateNewAccountInfo_Phonenumber_InValidPhoneNumber");

    const [createAccount, setCreateAccount] = useState({
        email: "",
        password: "",
        firstName: "",
        lastName: "",
        companyName: "",
        phoneNumber: "",
        ext: "",
    });
    const [errorMessage, setErrorMessage] = useState("");
    const [emailError, setEmailError] = useState(emailRequiredFieldMessage);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [confirmPassword, setConfirmPassword] = useState("");
    const [passwordErrorMessage, setPasswordErrorMessage] = useState<ReactNode>("");
    const [confirmPasswordErrorMessage, setConfirmPasswordErrorMessage] = useState<ReactNode>("");
    const [firstNameError, setFirstNameError] = useState(createAccount.firstName ? "" : firstNameRequiredFieldMessage);
    const [lastNameError, setLastNameError] = useState(createAccount.lastName ? "" : firstNameRequiredFieldMessage);
    const [companyNameError, setCompanyNameError] = useState(
        createAccount.companyName ? "" : firstNameRequiredFieldMessage,
    );
    const [phoneNumberError, setPhoneNumberError] = useState(createAccount.phoneNumber ? "" : phoneFieldMessage);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const recaptchaRef: RefObject<ReCAPTCHA> = React.createRef();

    const {
        passwordMinimumLength,
        passwordRequiresUppercase,
        passwordRequiresLowercase,
        passwordRequiresDigit,
        passwordRequiresSpecialCharacter,
    } = props.accountSettings;

    const validateEmailForCreateAccount = (email: string) => {
        const errorMessage = !email ? emailRequiredFieldMessage : validateEmail(email) ? "" : emailFieldMessage;
        setEmailError(errorMessage);
        return !errorMessage;
    };

    const validateConfirmPassword = (newPassword: string, newConfirmPassword: string) => {
        const errorMessage =
            newPassword !== newConfirmPassword
                ? siteMessage("CreateNewAccountInfo_PasswordCombination_DoesNotMatch")
                : "";
        setConfirmPasswordErrorMessage(errorMessage);
        return !errorMessage;
    };

    const validateFirstName = (name: string) => {
        const errorMessage = !name ? firstNameRequiredFieldMessage : "";
        setFirstNameError(errorMessage);
        return !errorMessage;
    };

    const validateLastName = (name: string) => {
        const errorMessage = !name ? lastNameRequiredFieldMessage : "";
        setLastNameError(errorMessage);
        return !errorMessage;
    };

    const validateCompanyName = (name: string) => {
        const errorMessage = !name ? companyNameRequiredFieldMessage : "";
        setCompanyNameError(errorMessage);
        return !errorMessage;
    };

    const validatePhone = (phone: string) => {
        const errorMessage = !phone ? phoneFieldMessage : "";
        setPhoneNumberError(errorMessage);
        return !errorMessage;
    };

    const validateFormSubmitEnabled = () => {
        return !(
            emailError ||
            passwordErrorMessage ||
            confirmPasswordErrorMessage ||
            firstNameError ||
            lastNameError ||
            companyNameError
        );
    };

    const emailChangeHandler = (email: string) => {
        validateEmailForCreateAccount(email);
        setCreateAccount({ ...createAccount, email });
    };

    const passwordChangeHandler = (newPassword: string) => {
        validateConfirmPassword(newPassword, confirmPassword);
        validatePassword({
            password: newPassword,
            onComplete: errorMessage => {
                setPasswordErrorMessage(errorMessage);
            },
        });
        setCreateAccount({ ...createAccount, password: newPassword });
    };

    const confirmPasswordChangeHandler = (newConfirmPassword: string) => {
        setConfirmPassword(newConfirmPassword);
        validateConfirmPassword(createAccount.password, newConfirmPassword);
    };

    const firstNameChangeHandler = (name: string) => {
        validateFirstName(name);
        setCreateAccount({ ...createAccount, firstName: name });
    };

    const lastNameChangeHandler = (name: string) => {
        validateLastName(name);
        setCreateAccount({ ...createAccount, lastName: name });
    };

    const companyNameChangeHandler = (name: string) => {
        validateCompanyName(name);
        setCreateAccount({ ...createAccount, companyName: name });
    };

    const phoneNumberChangeHandler = (phone: string) => {
        validatePhone(phone);
        setCreateAccount({ ...createAccount, phoneNumber: phone });
    };

    const phoneNumberExtensionChangeHandler = (ext: string) => {
        setCreateAccount({ ...createAccount, ext });
    };

    const isSubmitEnabled = validateFormSubmitEnabled();

    return (
        <>
            <StyledForm noValidate>
                <GridContainer>
                    <GridItem>
                        <GridContainer>
                            <GridItem>
                                <Typography>Create Your Account</Typography>
                                <StyledWrapper></StyledWrapper>
                            </GridItem>
                            <GridItem>
                                <Typography>Privacy Policy</Typography>
                                <Icon />
                            </GridItem>
                        </GridContainer>
                        <GridContainer>
                            <GridItem>
                                <TextField
                                    type="email"
                                    label={translate("Email Address")}
                                    onChange={e => emailChangeHandler(e.currentTarget.value)}
                                    value={createAccount.email}
                                    data-test-selector="createAccount_email"
                                    required
                                    error={isSubmitted && emailError}
                                />
                            </GridItem>
                            <GridItem>
                                <TextField
                                    id="password"
                                    type="password"
                                    label={translate("Password")}
                                    value={createAccount.password}
                                    onChange={e => passwordChangeHandler(e.currentTarget.value)}
                                    error={isSubmitted && passwordErrorMessage}
                                    data-test-selector="createAccount_password"
                                    required
                                />
                            </GridItem>
                            <GridItem>
                                <TextField
                                    id="confirm-password"
                                    type="password"
                                    label={translate("Confirm Password")}
                                    value={confirmPassword}
                                    onChange={e => confirmPasswordChangeHandler(e.currentTarget.value)}
                                    error={isSubmitted && confirmPasswordErrorMessage}
                                    data-test-selector="createAccount_confirmPassword"
                                    required
                                />
                            </GridItem>
                            <GridItem>
                                <TextField
                                    label={translate("First Name")}
                                    value={createAccount.firstName}
                                    data-test-selector="createAccount_firstName"
                                    onChange={e => firstNameChangeHandler(e.currentTarget.value)}
                                    error={isSubmitted && firstNameError}
                                    required
                                />
                            </GridItem>
                            <GridItem>
                                <TextField
                                    label={translate("Last Name")}
                                    value={createAccount.lastName}
                                    data-test-selector="createAccount_lastName"
                                    onChange={e => lastNameChangeHandler(e.currentTarget.value)}
                                    error={isSubmitted && lastNameError}
                                    required
                                />
                            </GridItem>
                            <GridItem>
                                <TextField
                                    label={translate("Company Name")}
                                    value={createAccount.companyName}
                                    data-test-selector="createAccount_companyName"
                                    onChange={e => companyNameChangeHandler(e.currentTarget.value)}
                                    error={isSubmitted && companyNameError}
                                    required
                                />
                            </GridItem>
                            <GridItem>
                                <GridContainer>
                                    <GridItem>
                                        <TextField
                                            label={translate("Phone Number")}
                                            value={createAccount.phoneNumber}
                                            data-test-selector="createAccount_phoneNumber"
                                            onChange={e => phoneNumberChangeHandler(e.currentTarget.value)}
                                            error={isSubmitted && phoneNumberError}
                                        />
                                    </GridItem>
                                    <GridItem>
                                        <TextField
                                            label={translate("Ext.")}
                                            value={createAccount.ext}
                                            data-test-selector="createAccount_ext"
                                            onChange={e => phoneNumberExtensionChangeHandler(e.currentTarget.value)}
                                        />
                                    </GridItem>
                                    {errorMessage && (
                                        <GridItem data-test-selector="createAccount_ErrorMessage">
                                            <Typography>{errorMessage}</Typography>
                                        </GridItem>
                                    )}
                                    <GridItem>
                                        <ReCaptcha recaptchaRef={recaptchaRef} location="CreateAccount" />
                                    </GridItem>
                                    <GridItem>
                                        <GridContainer>
                                            <GridItem>
                                                <Link>Cancel</Link>
                                            </GridItem>
                                            <GridItem>
                                                <Button
                                                    type="submit"
                                                    disabled={isSubmitting || (isSubmitted && !isSubmitEnabled)}
                                                    data-test-selector="createAccount_createButton"
                                                >
                                                    {translate("Save My Information")}
                                                </Button>
                                            </GridItem>
                                        </GridContainer>
                                    </GridItem>
                                </GridContainer>
                            </GridItem>
                        </GridContainer>
                    </GridItem>
                    <GridItem>
                        <GridContainer>
                            <Typography>{translate("Password Requirements")}</Typography>
                            <StyledWrapper></StyledWrapper>
                            <GridContainer>
                                <GridItem>
                                    {translate(
                                        "Must be at least {0} characters long",
                                        passwordMinimumLength.toString(),
                                    )}
                                </GridItem>
                                {passwordRequiresDigit && (
                                    <GridItem>{translate("Include at least one number")}</GridItem>
                                )}
                                {passwordRequiresUppercase && (
                                    <GridItem>{translate("Include at least one uppercase character")}</GridItem>
                                )}
                                {passwordRequiresLowercase && (
                                    <GridItem>{translate("Include at least one lowercase character")}</GridItem>
                                )}
                                {passwordRequiresSpecialCharacter && (
                                    <GridItem>
                                        {translate("Password must include at least one non alphanumeric character")}
                                    </GridItem>
                                )}
                            </GridContainer>
                        </GridContainer>
                    </GridItem>
                </GridContainer>
            </StyledForm>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(GlobalCreateAccount),
    definition: {
        displayName: "Global Create Account Menu",
        allowedContexts: ["CreateAccountPage"],
        group: "Common",
    },
};

export default widgetModule;
